import { Component, OnInit, inject } from '@angular/core';
import { ToasterService, TranslateService } from '@devinforius/i-essentials';
import { AuthService } from './core/services/authentication.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { INavbarConfig } from '@devinforius/i-essentials/lib/interfaces';
import { Language } from '@shared/types';
import { forkJoin } from 'rxjs';
import { GlobalService } from './core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private authService: AuthService = inject(AuthService);
  private oAuthService: OAuthService = inject(OAuthService);
  private router: Router = inject(Router);
  private translateService: TranslateService = inject(TranslateService);
  private toasterService: ToasterService = inject(ToasterService);
  private globalService: GlobalService = inject(GlobalService);

  public loading: boolean = true;
  public isConnected: boolean = false;
  public config!: INavbarConfig;

  public async ngOnInit(): Promise<void> {
    await this.initTranslation();

    this.translateService.onTranslationReady.subscribe((value: boolean) => {
      if (value) {
        this.reloadApp();
        this.setINavBarConfig();
      }
    });

    this.authService.isConnected.subscribe((response: boolean) => {
      this.isConnected = response || this.oAuthService.hasValidAccessToken();

      if (this.isConnected) {
        this.loadUser();
      }
    });
  }

  public logout(): void {
    this.oAuthService.logOut();
  }

  private loadUser(): void {
    this.loading = true;
    
    this.authService.getMe().subscribe({
      next: () => {
        this.loadGlobal();
        this.setINavBarConfig();
      },
      error: () => {
        this.toasterService.push('error', this.translateService.translate('global.error'), this.translateService.translate('auth.error.get-me'));
        this.loading = false;
        this.loadGlobal();
      }
    });
  }

  private loadGlobal(): void {
    this.loading = true;
    
    forkJoin([
        this.globalService.getNatures(),
        this.globalService.getSources(),
        this.globalService.getStatus(),
        this.globalService.getTypes()
      ]).subscribe({
        next: () => {
          this.loading = false;
          if (this.router.url.startsWith('/callback')) {
            this.router.navigate(['valves']);
          }
        },
        error: () => {
          this.toasterService.push('error', this.translateService.translate('global.error'), this.translateService.translate('global.error-dictionnary'));
          this.router.navigate(['valves']);
        }
      });
  }

  private setINavBarConfig(): void {
    this.config = {
      names: {
        app: 'iValve',
        home: this.authService.profile?.tenantName ?? '-',
        user: `${this.authService.profile?.lastName ?? ''} ${this.authService.profile?.firstName ?? ''}` 
      },
      links: {
        navLinks: [
          {
            icon: 'bi-table',
            title: this.translateService.translate('global.valves'),
            path: 'valves',
            click: () => this.router.navigate(['valves'])
          },
        ],
      },
      routes: { assistance: '/assistance' },
      menus: {
        userMenuItems: [],
        langMenuItems: [
          {
            icon: '../assets/flags/fr.svg',
            label: 'Français',
            command: async () => {
              this.updateLanguage('fr');
            },
          },
          {
            icon: '../assets/flags/nl.svg',
            label: 'Nederlands',
            command: async () => {
              this.updateLanguage('nl');
            },
          },
          {
            icon: '../assets/flags/en.svg',
            label: 'English',
            command: async () => {
              this.updateLanguage('en');
            },
          },
          {
            icon: '../assets/flags/de.svg',
            label: 'Deutsch',
            command: async () => {
              this.updateLanguage('de');
            },
          },
        ],
      },
      isSmallSidebar: false,
      appColor: '#FFF',
      showButtons: { admin: true, languages: true },
    };
  }

  private async updateLanguage(lang: Language): Promise<void> {
    this.loading = true;
    localStorage.setItem('lang', lang);

    await this.translateService.setCurrentLang(lang);
  }

  private async initTranslation(): Promise<void> {
    this.loading = true;

    const lang: string | null = localStorage.getItem('lang');
    const allLanguages: Language[] = ['en', 'nl', 'de', 'fr'];

    if (!lang || lang === 'undefined' || lang === 'null') {
      const userLang: string = navigator.language
        .split('-')
        .reverse()[0]
        .toLowerCase();
      localStorage.setItem(
        'lang',
        allLanguages.indexOf(userLang as Language) >= 0 ? userLang : 'fr'
      );
    }

    await this.translateService.init(
      localStorage.getItem('lang') as string,
      allLanguages,
      ['assets/translations', 'assets/i18n']
    );

    this.toasterService.setKey('ivalve-toast');
    this.reloadApp();
  }

  private reloadApp(): void {
    this.loading = true;

    setTimeout(() => {
      this.loading = false;
    }, 0);
  }
}
